import {
  err,
  ok,
  parseVideoDetails,
  resolveInstanceUrl,
  type AbortLike,
  type Result,
  type VideoDetails,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { fetchJson } from './json';
import type { ApiProblem } from './problem';

/**
 * Endpoint calls: build the URL through core (R2 — no hand-concatenated instance URLs), send it
 * through the transport (retry + classification), read the body through `api/json.ts`
 * (R4 — core never parses JSON text), then hand the value to core's parser.
 *
 * `ApiProblem` is declared in `api/problem.ts` since 4a — beside the sentences it turns into — and
 * re-exported here because eleven modules and every screen import it from this path. Moving the
 * *declaration* was what let the copy table be tested without dragging the browser in behind it; moving
 * the import sites would have been forty files of churn for nothing.
 */
export type { ApiProblem } from './problem';

export async function fetchVideoDetails(
  platform: Platform,
  videoId: string,
  // `AbortLike`, not `AbortSignal`: screen models call this and must stay DOM-free (R13).
  signal?: AbortLike,
): Promise<Result<VideoDetails, ApiProblem>> {
  const path = `/api/v1/videos/${encodeURIComponent(videoId)}`;
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });

  // The empty-body check that used to be written out here is `api/json.ts` since 4a — same reading,
  // one copy, and now true of the six endpoint modules that did not have it.
  const raw = await fetchJson(platform, url, signal);
  if (!raw.ok) return err(raw.error);

  const parsed = parseVideoDetails(raw.value, platform.parseContext);
  if (!parsed.ok) return err({ kind: 'parse', error: parsed.error });

  return ok(parsed.value);
}
